﻿import { invalidate, useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
    CanvasTexture,
    DoubleSide,
    InstancedBufferAttribute,
    type InstancedMesh,
    Matrix4,
    PlaneGeometry,
    Quaternion,
    ShaderMaterial,
    Vector3
} from 'three';

import {
    FONT_SIZE,
    type Label,
    buildAtlas
} from 'app/modules/wallforming/components/scenes/objects/dimensions/buildAtlas';

import type { DimensionPanel } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionPanel';

interface PanelDimensionTextsProps {
    panels: DimensionPanel[];
}

// ---------------------------------------------------------------------------
// Text shader  –  world-space, coplanar with the panel face
//
// The instance matrix carries the panel position + orientation (u/v/n axes).
// A separate per-instance attribute `textScale` holds the actual text-quad
// dimensions so the instance matrix does not need to encode scale.
// ---------------------------------------------------------------------------
const TEXT_VERT = /* glsl */ `
    attribute vec4 uvRect;    // (u0, v0, uw, vh) in [0..1] atlas space
    attribute vec2 textScale; // (world_width, world_height) of text quad in metres
    varying   vec2 vUv;

    void main() {
        // planeGeometrymetry has vertices at (±0.5, ±0.5, 0).
        // Scale by textScale; a tiny +Z offset keeps text just in front of the
        // border quad (both are in the same panel-face plane).
        vec4 localPos = vec4(position.x * textScale.x, position.y * textScale.y, 0.005, 1.0);
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * localPos;

        // Flip V: canvas Y grows downward, UV Y grows upward (flipY=false).
        vec2 flippedUv = vec2(uv.x, 1.0 - uv.y);
        vUv = flippedUv * uvRect.zw + uvRect.xy;
    }
`;

const TEXT_FRAG = /* glsl */ `
    uniform sampler2D atlas;
    varying vec2 vUv;

    void main() {
        vec4 s = texture2D(atlas, vUv);
        if (s.a < 0.05) discard;
        // Atlas is white text on transparent background; use red channel as mask
        // and render black text.
        gl_FragColor = vec4(0.0, 0.0, 0.0, s.r);
    }
`;

const planeGeometry = new PlaneGeometry(1, 1);
const scale = new Vector3(1, 1, 1);
const rotationMatrix = new Matrix4();
const quaternion = new Quaternion();
const position = new Vector3();
const matrix = new Matrix4();

const xAxis = new Vector3(0, 0, 0);
const yAxis = new Vector3(0, 0, 0);
const zAxis = new Vector3(0, 0, 0);

export const PanelDimensionTexts = ({ panels }: PanelDimensionTextsProps) => {
    const textMeshRef = useRef<InstancedMesh | null>(null);
    const prevTextureRef = useRef<CanvasTexture<HTMLCanvasElement> | null>(null);
    const prevMaterialRef = useRef<ShaderMaterial | null>(null);

    const textKey = useMemo(
        () => panels.map((p) => `${p.text}\0${Math.round((p.maxWidth / p.fontSize) * FONT_SIZE)}`).join('\0'),
        [panels]
    );

    const labelsForAtlas: Label[] = useMemo(
        () =>
            panels.map((p) => ({
                text: p.text,
                fontSize: p.fontSize,
                maxWidth: p.maxWidth
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [textKey]
    );

    const { texture, uvRects, atlasWidth, atlasHeight } = useMemo(() => buildAtlas(labelsForAtlas), [labelsForAtlas]);

    const textMaterial = useMemo(() => {
        // Dispose previous material and texture if they exist
        if (prevMaterialRef.current) {
            prevMaterialRef.current.dispose();
        }
        if (prevTextureRef.current) {
            prevTextureRef.current.dispose();
        }

        const material = new ShaderMaterial({
            depthWrite: false,
            fragmentShader: TEXT_FRAG,
            side: DoubleSide,
            transparent: true,
            uniforms: { atlas: { value: texture } },
            vertexShader: TEXT_VERT
        });

        prevMaterialRef.current = material;
        prevTextureRef.current = texture;
        return material;
    }, [texture]);

    // cleanup on unmount
    useEffect(() => {
        const planeMesh = textMeshRef.current;
        
        return () => {
            if (prevMaterialRef.current) {
                prevMaterialRef.current.dispose();
            }
            if (prevTextureRef.current) {
                prevTextureRef.current.dispose();
            }

            if (planeMesh) {
                planeMesh.geometry.dispose();
            }
        };
    }, []);

    useEffect(() => {
        const mesh = textMeshRef.current;
        if (!mesh || panels.length === 0) {
            return;
        }

        const uvArray = new Float32Array(panels.length * 4);
        const textScaleArray = new Float32Array(panels.length * 2);
        const worldScale = 1 / FONT_SIZE;

        for (let i = 0; i < panels.length; i++) {
            const panel = panels[i];
            const maxWidthPx = Math.round((panel.maxWidth / panel.fontSize) * FONT_SIZE);
            const key = `${panel.text}\0${maxWidthPx}`;
            const uv = uvRects.get(key);
            if (!uv) {
                continue;
            }

            // update scale - map atlas pixel count back to world units via fontsize
            const scaleX = uv.width * atlasWidth * panel.fontSize * worldScale;
            const scaleY = uv.height * atlasHeight * panel.fontSize * worldScale;

            xAxis.set(panel.u.x, panel.u.y, panel.u.z);
            yAxis.set(panel.v.x, panel.v.y, panel.v.z);
            zAxis.set(panel.normal.x, panel.normal.y, panel.normal.z);
            rotationMatrix.makeBasis(xAxis, yAxis, zAxis);

            quaternion.setFromRotationMatrix(rotationMatrix);
            position.set(panel.center.x, panel.center.y, panel.center.z);
            matrix.compose(position, quaternion, scale);
            mesh.setMatrixAt(i, matrix);

            textScaleArray[i * 2 + 0] = scaleX;
            textScaleArray[i * 2 + 1] = scaleY;

            uvArray[i * 4 + 0] = uv.x;
            uvArray[i * 4 + 1] = uv.y;
            uvArray[i * 4 + 2] = uv.width;
            uvArray[i * 4 + 3] = uv.height;
        }

        mesh.geometry.setAttribute('uvRect', new InstancedBufferAttribute(uvArray, 4));
        mesh.geometry.setAttribute('textScale', new InstancedBufferAttribute(textScaleArray, 2));
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
        invalidate();
    }, [panels, uvRects, atlasWidth, atlasHeight]);

    // Keep atlas uniform in sync when texture reference is refreshed
    useFrame(() => {
        if (textMaterial.uniforms.atlas.value !== texture) {
            textMaterial.uniforms.atlas.value = texture;
            textMaterial.needsUpdate = true;
        }
    });

    if (panels.length === 0) {
        return null;
    }

    return (
        <instancedMesh
            ref={textMeshRef}
            args={[planeGeometry, textMaterial, panels.length]}
            frustumCulled={false}
            renderOrder={2}
        />
    );
};
